import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import {
  Card,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useEffect, useState } from 'react';
import { Button, useDataProvider } from 'react-admin';
import { useController } from 'react-hook-form';

import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

const inputGlobalStyles = (
  <GlobalStyles
    styles={{
      article: {
        minWidth: '250px',
        '& >div:first-child': {
          '& > div:last-child': {
            '& span': {
              fontSize: '14px',
              '&:first-of-type': {
                width: 'min-content',
              },
            },
          },
        },
      },
    }}
  />
);

function not(a, b) {
  return a.filter(value => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter(value => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

export const CustomTransferList = ({ name, label }) => {
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(true);
  const { field } = useController({ name });

  useEffect(() => {
    let isMounted = true;

    const getMembers = () => {
      dataProvider
        .getList('members', {
          pagination: { page: 1, perPage: 1000 },
          sort: { order: 'ASC' },
          filter: {},
        })
        .then(({ data }) => {
          if (isMounted) {
            if (field && field.value) {
              const rightData = data.filter(item =>
                field.value.includes(item.id)
              );
              const leftData = data.filter(
                item => !field.value.includes(item.id)
              );
              setRight(rightData);
              setLeft(leftData);
            } else {
              setLeft(data);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });
    };

    if (left.length === 0) getMembers();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = value => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = items => intersection(checked, items).length;

  const handleToggleAll = items => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
    const updatedRight = right.concat(leftChecked);
    setRight(updatedRight);
    field.onChange(updatedRight.map(item => item.id));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
    const newRight = not(right, rightChecked);
    setRight(newRight);
    field.onChange(newRight.map(item => item.id));
  };

  const customList = (title, items) => (
    <Card component="article">
      {inputGlobalStyles}
      <CardHeader
        sx={{ bgcolor: 'background.default', px: 2, py: 1, fontSize: '1rem' }}
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} seleccionados`}
      />
      <Divider />
      <List
        sx={{
          height: '300px',
          bgcolor: '#fafafa',
          overflow: 'auto',
          '::-webkit-scrollbar': {
            width: '12px',
            border: '1px solid red',
          },
          '::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '50px',
          },
          '::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '50px',
          },
          '&::-webkit-scrollbar-button': {
            display: 'none',
            height: 0,
            width: 0,
          },
          scrollbar: {
            border: '1px solid red',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#fff #eaeaea',
          scrollMargin: 'none',
          scrollPadding: 'none',
        }}
        dense
        component="div"
        role="list"
      >
        {items.map(value => {
          const labelId = `transfer-list-all-item-${value.id}-label`;
          return (
            <ListItemButton
              key={value.id}
              role="listitem"
              onClick={handleToggle(value)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </ListItemIcon>
              <Typography>
                <ListItemText
                  id={labelId}
                  primary={
                    <>
                      {value.username}
                      {value.team_active?.[0]?.name && (
                        <>
                          {' '}
                          |{' '}
                          <span style={{ fontWeight: 'bold' }}>
                            {value.team_active[0].name}
                          </span>
                        </>
                      )}
                    </>
                  }
                />
              </Typography>
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );
  if (loading) return null;

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      //   {...field}
      label={label}
      // helperText={(isTouched || isSubmitted) && invalid ? error?.message : ''}
      sx={{ paddingTop: '0', flexWrap: 'nowrap' }}
    >
      <Grid item>{customList('Redactores', left)}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5, '& span': { mx: 0 } }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            <ArrowRight />
          </Button>
          <Button
            sx={{ my: 0.5, '& span': { mx: 0 } }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            <ArrowLeft />
          </Button>
        </Grid>
      </Grid>
      <Grid item>{customList('Redactores seleccionados', right)}</Grid>
    </Grid>
  );
};
